/**
 * The constructors — a fresh set, a fresh exercise from a library pick, a fresh day.
 *
 * Ported from `move-with-me-frontend/.../plans/builder/model/draftFactory.ts`. The day and exercise
 * constructors arrived first, because "+ Trening" on an empty weekday and the library rail's insert
 * both land here.
 *
 * **`createPhase` and `createEmptyDraft` arrived with the create modal (05.08).** They were held
 * back while the plan library had no "new plan" affordance and this app had no create route — both
 * would have been constructors with one hypothetical caller, the same reason PR-B1 recorded the
 * POST path as a gap. `features/create-plan` is that caller, and the gap is closed.
 *
 * ── Every constructor mints a fresh slot token, and that is the whole reason they exist ──────
 * Document-model ADR §3. A freshly picked exercise is a brand-new slot the backend has never
 * stored, which is exactly right: it reads as a first exposure, its activation is assigned
 * server-side, and it inherits nobody's progression chain. Making the only way to construct one a
 * factory that mints is what turns that rule from something a contributor must remember into
 * something they cannot get wrong.
 *
 * ── Two divergences from the original ────────────────────────────────────────────────────────
 * · **`progressionMetric` is left `null` rather than seeded from the measured axes.** Mobile calls
 *   `defaultAxisMetric(fields)` to pick `Weight`, or the first effort axis on a movement that
 *   measures no load. That module is the progression axis vocabulary and it belongs with PR-D's
 *   progression editor; seeding an axis here without the editor that can change it would pin a
 *   choice the trainer cannot see or correct. `null` is what `progressionRuleWire` already reads as
 *   the backend default, and it is what a plan authored before the field existed carries.
 * · **`category` is not defaulted** — this app carries the wire's own value verbatim and never
 *   mints one (see `PlanDraft.category`).
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import { builderStartingColumns, measurementFieldsFromWire } from '~/shared/measurement';

import { DEFAULT_DAY_REST_SECONDS } from './dayRest';
import { planCategoryToApi, type PlanCategoryKey } from './planCategory';
import type {
  DayDraft,
  ExerciseDraft,
  PhaseDraft,
  PhaseType,
  PlanDraft,
  PlanLiveContext,
  PlanOrigin,
  PlanSchedulingMode,
  SetDraft,
} from './planDraft.types';
import { PRESCRIPTION_MODE } from './prescriptionMode';
import { NO_PROGRESSION_RULES } from './progressionRules';
import { NO_STORED_RULES } from './progressionWire';
import { WORKING_SET_TYPE } from './setShape';
import { WARMUP_SET_TYPE } from './setTypeChange';
import { newSetLocalId, newSlotToken } from './slotToken';

/**
 * The backend `ExerciseRole` values this app AUTHORS. The enum has six — `Accessory`, `Cooldown`,
 * `Mobility` and `Finisher` also exist and are carried verbatim when they arrive from the phone —
 * but the canon's day view has two blocks, so these are the two a web surface can produce.
 */
export type AuthorableExerciseRole = 'Main' | 'Warmup';

/** The minimal exercise shape a pick seeds from — the library row, whichever slice produced it. */
export interface ExercisePick {
  readonly id: number;
  /** DATA, already resolved into the reader's language. Never i18n. */
  readonly name: string;
  /** Display-only type, drives a card glyph. `null` when unknown. */
  readonly typeKey: string | null;
  /** The exercise's measurement contract, however the source spelled it. Parsed here, once. */
  readonly measurementFields: readonly string[];
}

/** A new day's session length. A new day is a new decision, so nothing is inherited from its neighbour. */
const DEFAULT_DURATION_MINUTES = 60;

/**
 * A blank prescribed set: every metric null, the flat default rest, and a type.
 *
 * The rest is the FLAT default because a set has no day context of its own. Every insertion path
 * that does have one stamps the day's value over it (`restampExerciseRest`), so a new exercise on a
 * 120-second day never keeps the 90.
 */
export function createSet(setIndex: number, isWarmup = false): SetDraft {
  return {
    localId: newSetLocalId(),
    setIndex,
    setType: isWarmup ? WARMUP_SET_TYPE : WORKING_SET_TYPE,
    prescribedWeight: null,
    prescribedReps: null,
    prescribedRpe: null,
    prescribedRir: null,
    prescribedRestTime: DEFAULT_DAY_REST_SECONDS,
    prescribedDurationSeconds: null,
    prescribedDistanceMeters: null,
    prescribedRounds: null,
    prescribedPercent: null,
    prescribedTempo: null,
    prescribedSpeed: null,
    note: null,
    // No ceiling until a `PreviousExecution` rule exists to be capped, and this app authors neither.
    prescribedCeiling: null,
  };
}

/**
 * How many sets a freshly inserted exercise opens with.
 *
 * **Three, not one.** Mobile opens with a single set because a phone screen has room to add rows
 * one tap at a time; the canon's set table shows four and five rows per exercise, and a trainer
 * programming a strength day is going to want at least three. Every one of them is a plain
 * `Working` set with no values, so nothing is prescribed that the trainer did not type — the count
 * is a starting shape, not a prescription.
 *
 * A warm-up-role exercise still opens with one: a warm-up position is usually a single entry, and
 * three empty warm-up rows is noise in a block the canon renders as a name list.
 */
const DEFAULT_WORK_SET_COUNT = 3;

/**
 * Seed an exercise from a library pick.
 *
 * Only fields the contract NAMES are kept, through the app's one parser and in the dictionary's one
 * order. Nothing is dropped for being unrenderable: the contract is what the movement measures, and
 * which of those get a column is the separate question the column rule answers.
 */
export function createExercise(
  pick: ExercisePick,
  role: AuthorableExerciseRole = 'Main',
  group: string | null = null,
): ExerciseDraft {
  const fields = measurementFieldsFromWire(pick.measurementFields);
  const isWarmup = role === 'Warmup';
  const setCount = isWarmup ? 1 : DEFAULT_WORK_SET_COUNT;
  return {
    // A brand-new slot. The backend does not know this token and reads it as a first exposure.
    slotToken: newSlotToken(),
    exerciseId: pick.id,
    name: pick.name,
    // No activation yet — the backend assigns one on save. Until then it bases on its phase's start.
    activationCycle: null,
    typeKey: pick.typeKey,
    role,
    measurementFields: fields,
    // A brand-new exercise carries NO rule: every column rides flat until the trainer authors one.
    // An empty map is the ONE spelling of that, here and off the wire alike — the serialiser emits
    // an empty document, which the backend reads as its flat default.
    progressionRules: NO_PROGRESSION_RULES,
    unreadableProgressionRules: NO_STORED_RULES.unreadable,
    realMaxMultiplier: null,
    realMaxSource: null,
    // A fresh exercise runs for the whole of its day's window: no end, no holes. Both are AUTHORED
    // fields with no editor yet, so the seed is what the trainer gets until the gestures land.
    retirementCycle: null,
    excludedCycles: [],
    // Nothing has resolved it yet — the server answers on the next load.
    serverCycles: null,
    progressionOverrides: [],
    prescriptionMode: PRESCRIPTION_MODE.absolute,
    group,
    notes: null,
    // Starts on the fields the dictionary marks visible by default. From here its own values answer
    // the question.
    revealedColumns: builderStartingColumns(fields),
    // A fresh pick FOLLOWS its day's rest, so the column starts hidden and the caller stamps the
    // day's value onto its sets on insert.
    showsRestColumn: false,
    sets: Array.from({ length: setCount }, (_unused, index) => createSet(index + 1, isWarmup)),
    // A slot nothing has ever been given at. `null` rather than `[]`, for the reason the field
    // states: `[]` claims a live assignment answered and found nothing, and this row exists on no
    // server at all.
    executions: null,
  };
}

/**
 * The two scheduling seeds a day falls back on when the canvas it is being placed on needs a
 * coordinate it has none for. Mobile's `DEFAULT_DAY_OF_WEEK` and `DEFAULT_REST_DAYS_AFTER`.
 *
 * Both are still `export`ed rather than file-private: `createDay` is the only reader left now that
 * the mode flip is gone, and shrinking them to constants would hide the pair that has to agree.
 */
export const DEFAULT_DAY_OF_WEEK = 1;
export const DEFAULT_REST_DAYS_AFTER = 1;

/**
 * How long a brand-new phase runs. Four cycles — a month of weeks on a date-based plan, four
 * repetitions of the cycle on a sequence one.
 *
 * A LENGTH, not a prescription: it is the shortest block a progression rule has room to move
 * anything across, and it is what the phase bar draws before the trainer touches it. One cycle
 * would make the progression matrix a single column, which reads as if the feature were missing.
 */
const DEFAULT_PHASE_CYCLES = 4;

/**
 * A fresh phase, with one empty day in it.
 *
 * `serverId` is null, which is what tells the backend this phase is new when the tree is submitted
 * — `PhaseGrowthPolicy` judges a submitted phase's id against the consumed prefix, and a null one
 * can never take a trained phase's place.
 *
 * `startCycle` is the caller's, because a phase does not know where it sits: the store recomputes
 * every phase's start after a structural edit, and a constructor guessing `1` would be wrong for
 * every phase but the first.
 *
 * The day is created rather than left out: `PostPhaseTemplateInput.Days` is `[MinLength(1)]`, so a
 * phase with no day is a tree the serialiser refuses (`DraftSerialisationError`, `emptyPhase`).
 * A phase that cannot be saved from the moment it exists is not a useful thing to construct.
 *
 * **The day is seeded here even though mobile's `createPhase` leaves `days: []`.** Both trees are
 * unsavable until an exercise lands in them, so the divergence costs nothing on the wire — what it
 * buys is that every phase in this app has the same anatomy whichever act made it, and the seeded
 * day draws the canon's "bez ćwiczeń — nie zapisze się" caption, which says what an empty phase is.
 *
 * `type` and `cycles` are the CALLER's, because the two acts that construct a phase want different
 * answers: a brand-new plan opens on a four-cycle Build block, and "+ Faza" appends whichever kind
 * the trainer picked at one cycle (`ADDED_PHASE_CYCLES` — see `phaseEdits.ts` for why one).
 */
export function createPhase(
  mode: PlanSchedulingMode,
  name: string,
  startCycle: number,
  type: PhaseType = 'build',
  cycles: number = DEFAULT_PHASE_CYCLES,
): PhaseDraft {
  return {
    serverId: null,
    name,
    type,
    cycles,
    startCycle,
    days: [createDay(mode)],
  };
}

/** The origin of every plan that is not a copy of a library template. One object, never re-minted. */
export const AUTHORED_ORIGIN: PlanOrigin = { kind: 'authored' };

/**
 * The live context of a plan nothing is behind — no client, no stored row, no concurrency token.
 *
 * `isForkTemplate: false` and `lastConsumedCycle: null` — a draft has no client, so nothing is
 * locked; `baseVersion: null`, because there is no stored row to race against and POST carries no
 * concurrency token; `serverStructure: null`, because there is no stored tree to measure a
 * structural edit against. Every one of those is what `structuralSaveRoute` reads to answer
 * `save-direct`, which is the correct answer for a plan that exists nowhere but this tab. Shared by
 * the two producers of an unsaved plan — a fresh seed and a template copy — so they cannot disagree.
 */
export function unsavedLiveContext(totalCycles: number): PlanLiveContext {
  return {
    isForkTemplate: false,
    clientName: null,
    firstOpenCycle: null,
    lastConsumedCycle: null,
    totalCycles,
    baseVersion: null,
    executionMode: null,
    serverStructure: null,
    cyclesClosedToNewDays: null,
  };
}

/** Everything the create flow decides about a plan before the builder opens on it. */
export interface EmptyDraftSeed {
  /** DATA, already trimmed by the caller. Never i18n — this is the trainer's own word. */
  readonly name: string;
  readonly category: PlanCategoryKey;
  readonly mode: PlanSchedulingMode;
  /** The first phase's name. DATA, so the caller resolves it — `model/` holds no sentences. */
  readonly phaseName: string;
  /**
   * What the program is for, as the trainer typed it. Optional because it is optional on the wire
   * and because the create dialog offers it as such; an empty or blank one is stored as `null`,
   * which is the same normalisation `draftToRequest` applies on the way out.
   */
  readonly description?: string;
  /**
   * The cover row the trainer picked out of the category's pool, or `undefined` for "let MoveBuddy
   * choose". The two are genuinely different answers: a chosen id travels on the save and pins the
   * image, while no id leaves `coverId` null and the plan shows its category's bundled fallback
   * (`model/planCover.ts`) until somebody picks one. Never defaulted to the pool's first entry —
   * that would be a choice the trainer did not make, stored as though they had.
   */
  readonly coverId?: number;
}

/**
 * A brand-new plan: one phase, one day, **no exercises**.
 *
 * ── It is deliberately UNSAVEABLE as constructed, and that is the design ─────────────────────
 * `POST /plan-template` refuses a plan without at least one real exercise, measured, and the create
 * modal has no exercise picker — the owner dropped autosave, so the plan is authored in the builder
 * and posted by its first Save. This seed is therefore a LOCAL draft (`id: null`), and the builder
 * gates Save until the trainer has put something in it (`firstEmptyPhaseName` in `draftToRequest`).
 * Seeding a placeholder exercise instead would be fabricated data on a plan the trainer would then
 * have to find and delete.
 *
 * ── The cover is assigned here, and never left null ──────────────────────────────────────────
 * Owner rule (4 Aug 2026): every plan has a cover, and it comes from its own category's pool. The
 * modal does not ask, so the seed derives it — deterministically from the name, never randomly, so
 * the image the trainer saw in step 2 is the image the plan keeps.
 *
 * ── `live` is the shape of a plan nothing is behind ──────────────────────────────────────────
 * See `unsavedLiveContext`.
 */
export function createEmptyDraft(seed: EmptyDraftSeed): PlanDraft {
  return {
    id: null,
    name: seed.name,
    description: seed.description?.trim() === '' ? null : (seed.description ?? null),
    // The WIRE's own spelling, because `PlanDraft.category` carries the wire value verbatim and a
    // whole-tree save re-emits exactly what the field holds.
    category: planCategoryToApi(seed.category),
    // The pool picker exists as of 21 September 2026 (`api/planCoverQueries.ts`), so a plan may
    // arrive here with a row already chosen. Without one the plan still shows its category's
    // bundled fallback (`planCover.ts`), which is what every plan created before today carries.
    coverId: seed.coverId ?? null,
    coverImageUrl: null,
    mode: seed.mode,
    // A template the trainer is building for their own library, not a client's fork.
    isLibraryEntry: true,
    origin: AUTHORED_ORIGIN,
    phases: [createPhase(seed.mode, seed.phaseName, 1)],
    live: unsavedLiveContext(DEFAULT_PHASE_CYCLES),
  };
}

/**
 * A fresh day for the given scheduling mode, with no exercises yet.
 *
 * Date-based days seed `dayOfWeek` and leave `restDaysAfter` null; sequence-based do the inverse —
 * so a day is always serialisable for its mode. The between-sets rest starts FLAT at the default
 * rather than inheriting the previous day's, for the same reason the duration does: a new day is a
 * new decision.
 *
 * `sessionName` is left null. The name is DATA and belongs to the trainer; `draftToRequest` falls
 * back to the day's name and then to a constant, so an unnamed day is savable and a minted default
 * would be a name the trainer never chose but would have to delete.
 */
export function createDay(mode: PlanSchedulingMode, dayOfWeek: number | null = null): DayDraft {
  const isDate = mode === 'date';
  return {
    slotToken: newSlotToken(),
    // A fresh day has no server id and no stored position: it was never stored anywhere, so the
    // structural preview reads it as newly added and never as a day the trainer moved.
    serverId: null,
    serverOrder: null,
    name: null,
    sessionName: null,
    sessionNotes: null,
    dayOfWeek: isDate ? (dayOfWeek ?? DEFAULT_DAY_OF_WEEK) : null,
    restDaysAfter: isDate ? null : DEFAULT_REST_DAYS_AFTER,
    // A fresh day runs for the WHOLE of its phase: no start, no end, no holes. That is what an
    // unwindowed day means on the wire, and it is the same plan a day was before the lifespan
    // existed — so an existing add-day affordance keeps behaving exactly as it did. The calendar's
    // gestures narrow it afterwards, deliberately, with the trainer's answer to a scope question.
    activationCycle: null,
    retirementCycle: null,
    excludedCycles: [],
    // Server-owned, and the two answer differently.
    //
    // `serverCycles` is the SERVER's resolution of a row it has never seen, so `null` — unanswered —
    // is the only honest value.
    //
    // `liveClosedCycles` is `[]`, and that is a statement rather than a default: a day this client
    // minted a moment ago exists nowhere, so the client has closed no cycle of it. `null` would mean
    // "the surface did not say", which sends every gate that reads it to the PHASE fallback — and
    // that is exactly the journey the 7 September 2026 round closed: on a fork whose first cycle the
    // client has trained, a trainer adds a day and then cannot put an exercise in it.
    // `e2e/plan-day-programming.spec.ts`'s stationary booking case walks it.
    serverCycles: null,
    liveClosedCycles: [],
    durationMinutes: DEFAULT_DURATION_MINUTES,
    restSeconds: DEFAULT_DAY_REST_SECONDS,
    exercises: [],
  };
}
